import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, Plus, Trash2, Settings as SettingsIcon, Users, Calendar, Heart, FileSpreadsheet, Edit, Check, X, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'pastor';
  const [prayerFreqs, setPrayerFreqs] = useState({ regular: '1x_week', visitor: '2x_week', occasional: '1x_week', inactive: 'monthly' });
  const [visitorThreshold, setVisitorThreshold] = useState(2);
  const [ministries, setMinistries] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [newMinistry, setNewMinistry] = useState('');
  const [editMinistry, setEditMinistry] = useState(null);
  const [editMinistryName, setEditMinistryName] = useState('');
  const [newEvent, setNewEvent] = useState({ name: '', day_of_week: 'Sunday', time: '11:00 AM' });
  const [editEvent, setEditEvent] = useState(null);
  const [editEventData, setEditEventData] = useState({});
  const [csvResult, setCsvResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'user' });
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [touchpointRules, setTouchpointRules] = useState({ rules: [] });
  const [absentWeeks, setAbsentWeeks] = useState(3);
  const [absentTypes, setAbsentTypes] = useState(['regular', 'occasional']);
  const fileRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/prayer/settings'),
      api.get('/settings'),
      api.get('/ministries'),
      api.get('/events'),
      isAdmin ? api.get('/users') : Promise.resolve({ data: [] }),
      api.get('/settings/touchpoint-rules'),
      api.get('/settings/absent'),
    ]).then(([ps, s, m, e, u, tr, ab]) => {
      if (ps.data.frequencies) setPrayerFreqs(prev => ({ ...prev, ...ps.data.frequencies }));
      setVisitorThreshold(s.data.visitor_threshold);
      setMinistries(m.data);
      setEvents(e.data);
      setUsers(u.data);
      setTouchpointRules(tr.data);
      if (ab.data) { setAbsentWeeks(ab.data.weeks || 3); setAbsentTypes(ab.data.types || ['regular', 'occasional']); }
    }).catch(console.error).finally(() => setLoading(false));
  }, [isAdmin]);

  const savePrayerSettings = async () => {
    try { await api.put('/prayer/settings', { frequencies: prayerFreqs }); toast.success('Prayer frequencies updated'); }
    catch { toast.error('Failed to update'); }
  };

  const saveVisitorThreshold = async () => {
    try { await api.put('/settings', { visitor_threshold: visitorThreshold }); toast.success('Threshold updated'); }
    catch { toast.error('Failed to update'); }
  };

  const addMinistry = async () => {
    if (!newMinistry.trim()) return;
    try {
      const res = await api.post('/ministries', { name: newMinistry.trim() });
      setMinistries(prev => [...prev, res.data]);
      setNewMinistry('');
      toast.success('Ministry added');
    } catch { toast.error('Failed to add ministry'); }
  };

  const deleteMinistry = async (id) => {
    try { await api.delete(`/ministries/${id}`); setMinistries(prev => prev.filter(m => m.ministry_id !== id)); toast.success('Ministry removed'); }
    catch { toast.error('Failed to delete'); }
  };

  const startEditMinistry = (m) => {
    setEditMinistry(m.ministry_id);
    setEditMinistryName(m.name);
  };

  const saveEditMinistry = async (id) => {
    if (!editMinistryName.trim()) return;
    try {
      const res = await api.put(`/ministries/${id}`, { name: editMinistryName.trim() });
      setMinistries(prev => prev.map(m => m.ministry_id === id ? res.data : m));
      setEditMinistry(null);
      toast.success('Ministry renamed');
    } catch { toast.error('Failed to rename'); }
  };

  const addEvent = async () => {
    if (!newEvent.name.trim()) return;
    try {
      const res = await api.post('/events', newEvent);
      setEvents(prev => [...prev, res.data]);
      setNewEvent({ name: '', day_of_week: 'Sunday', time: '11:00 AM' });
      toast.success('Event added');
    } catch { toast.error('Failed to add event'); }
  };

  const deleteEvent = async (id) => {
    try { await api.delete(`/events/${id}`); setEvents(prev => prev.filter(e => e.event_id !== id)); toast.success('Event removed'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const startEditEvent = (e) => {
    setEditEvent(e.event_id);
    setEditEventData({ name: e.name, day_of_week: e.day_of_week, time: e.time });
  };

  const saveEditEvent = async (id) => {
    try {
      const res = await api.put(`/events/${id}`, editEventData);
      setEvents(prev => prev.map(e => e.event_id === id ? res.data : e));
      setEditEvent(null);
      toast.success('Event updated');
    } catch { toast.error('Failed to update'); }
  };

  const changeRole = async (userId, role) => {
    try { await api.put(`/users/${userId}/role`, { role }); setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role } : u)); toast.success('Role updated'); }
    catch { toast.error('Failed to update role'); }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setCsvResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/import/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsvResult(res.data);
      toast.success(`Import complete: ${res.data.imported} new, ${res.data.updated} updated`);
    } catch { toast.error('Import failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 animate-fade-in">
      <Tabs defaultValue="prayer">
        <TabsList className="bg-slate-100 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="prayer" data-testid="settings-tab-prayer" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><Heart className="h-3.5 w-3.5 mr-1.5" />Prayer</TabsTrigger>
          <TabsTrigger value="thresholds" data-testid="settings-tab-thresholds" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />Thresholds</TabsTrigger>
          <TabsTrigger value="touchpoint-rules" data-testid="settings-tab-touchpoint-rules" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />Touchpoint Rules</TabsTrigger>
          <TabsTrigger value="csv" data-testid="settings-tab-csv" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />CSV Import</TabsTrigger>
          <TabsTrigger value="ministries" data-testid="settings-tab-ministries" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><Users className="h-3.5 w-3.5 mr-1.5" />Ministries</TabsTrigger>
          <TabsTrigger value="events" data-testid="settings-tab-events" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><Calendar className="h-3.5 w-3.5 mr-1.5" />Events</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="settings-tab-users" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><SettingsIcon className="h-3.5 w-3.5 mr-1.5" />Users</TabsTrigger>}
        </TabsList>

        <TabsContent value="prayer" className="mt-4">
          <Card className="border-slate-200 shadow-sm max-w-xl">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Prayer Rotation Frequency</CardTitle>
              <CardDescription>Set how often each attendee type should be prayed for</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {[
                { key: 'regular', label: 'Regular', color: 'text-blue-700' },
                { key: 'visitor', label: 'Visitor', color: 'text-amber-700' },
                { key: 'occasional', label: 'Occasional', color: 'text-teal-700' },
                { key: 'inactive', label: 'Inactive', color: 'text-slate-500' },
              ].map(({ key, label, color }) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Label className={`font-medium ${color}`}>{label}</Label>
                  </div>
                  <Select value={prayerFreqs[key] || '1x_week'} onValueChange={v => setPrayerFreqs(prev => ({ ...prev, [key]: v }))}>
                    <SelectTrigger data-testid={`prayer-freq-${key}`} className="w-52 border-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1x_week">1 time per week</SelectItem>
                      <SelectItem value="2x_week">2 times per week</SelectItem>
                      <SelectItem value="3x_week">3 times per week</SelectItem>
                      <SelectItem value="4x_week">4 times per week</SelectItem>
                      <SelectItem value="every_2_weeks">Every 2 weeks</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <Button data-testid="save-prayer-settings" onClick={savePrayerSettings} className="w-full bg-blue-700 hover:bg-blue-800 mt-2">Save Prayer Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="thresholds" className="mt-4">
          <div className="space-y-4 max-w-xl">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit']">Visitor-to-Regular Threshold</CardTitle>
                <CardDescription>Number of monthly attendances before recommending a visitor be promoted to regular status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <Input data-testid="visitor-threshold" type="number" value={visitorThreshold} onChange={e => setVisitorThreshold(parseInt(e.target.value) || 2)} className="w-20 border-slate-300" min={1} max={10} />
                  <span className="text-sm text-slate-600">times per month</span>
                </div>
                <Button data-testid="save-threshold" onClick={saveVisitorThreshold} className="bg-blue-700 hover:bg-blue-800">Save Threshold</Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit']">Absent Alert Settings</CardTitle>
                <CardDescription>Configure which types appear in the "Absent X+ Weeks" section on the Dashboard</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Label className="shrink-0">Weeks missed:</Label>
                  <Input data-testid="absent-weeks" type="number" value={absentWeeks} onChange={e => setAbsentWeeks(parseInt(e.target.value) || 3)} className="w-20 border-slate-300" min={1} max={12} />
                </div>
                <div>
                  <Label className="mb-2 block">Include these types:</Label>
                  <div className="flex flex-wrap gap-2">
                    {['regular', 'visitor', 'occasional', 'inactive'].map(t => (
                      <label key={t} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer text-sm capitalize ${absentTypes.includes(t) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-600'}`}>
                        <Checkbox checked={absentTypes.includes(t)} onCheckedChange={() => setAbsentTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])} className="h-3.5 w-3.5" />
                        {t}
                      </label>
                    ))}
                  </div>
                </div>
                <Button className="bg-blue-700 hover:bg-blue-800" onClick={async () => {
                  try { await api.put('/settings/absent', { weeks: absentWeeks, types: absentTypes }); toast.success('Absent settings saved'); }
                  catch { toast.error('Failed'); }
                }}>Save Absent Settings</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="csv" className="mt-4">
          <Card className="border-slate-200 shadow-sm max-w-xl">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Import People from CSV</CardTitle>
              <CardDescription>Upload CSV or Excel (.xlsx) files. Columns: First Name, Last Name, Phone Number, Address, City, State, Zip, Email, Birthday, Anniversary, Mobile, Secondary Phone, Ministries, Type of Attendee</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                <p className="text-sm text-slate-600 mb-3">Drop your CSV file here or click to browse</p>
                <input ref={fileRef} data-testid="csv-upload" type="file" accept=".csv,.xlsx,.xls" onChange={handleCsvUpload} className="hidden" />
                <Button data-testid="csv-browse-button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-slate-300">
                  {uploading ? 'Uploading...' : 'Choose File'}
                </Button>
              </div>
              {csvResult && (
                <div className="mt-4 p-4 rounded-lg bg-slate-50 space-y-1" data-testid="csv-results">
                  <p className="text-sm"><span className="font-medium text-emerald-700">{csvResult.imported}</span> new people imported</p>
                  <p className="text-sm"><span className="font-medium text-blue-700">{csvResult.updated}</span> existing records updated</p>
                  {csvResult.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-red-700">{csvResult.errors.length} error(s):</p>
                      {csvResult.errors.map((e, i) => <p key={`err-${i}-${e.slice(0,10)}`} className="text-xs text-red-600">{e}</p>)}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ministries" className="mt-4">
          <Card className="border-slate-200 shadow-sm max-w-xl">
            <CardHeader><CardTitle className="text-lg font-['Outfit']">Manage Ministries</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input data-testid="new-ministry-input" value={newMinistry} onChange={e => setNewMinistry(e.target.value)} placeholder="New ministry name" className="border-slate-300" onKeyDown={e => e.key === 'Enter' && addMinistry()} />
                <Button data-testid="add-ministry" onClick={addMinistry} className="bg-blue-700 hover:bg-blue-800 shrink-0"><Plus className="h-4 w-4 mr-1" />Add</Button>
              </div>
              <div className="space-y-2">
                {ministries.map(m => (
                  <div key={m.ministry_id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50">
                    {editMinistry === m.ministry_id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input data-testid={`edit-ministry-input-${m.ministry_id}`} value={editMinistryName} onChange={e => setEditMinistryName(e.target.value)} className="h-8 border-slate-300" onKeyDown={e => e.key === 'Enter' && saveEditMinistry(m.ministry_id)} />
                        <Button size="sm" variant="ghost" onClick={() => saveEditMinistry(m.ministry_id)} className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700"><Check className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditMinistry(null)} className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"><X className="h-3 w-3" /></Button>
                      </div>
                    ) : (
                      <>
                        <span className="text-sm text-slate-700">{m.name}</span>
                        <div className="flex items-center gap-1">
                          <Button data-testid={`edit-ministry-${m.ministry_id}`} variant="ghost" size="sm" onClick={() => startEditMinistry(m)} className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 h-7 w-7 p-0">
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button data-testid={`delete-ministry-${m.ministry_id}`} variant="ghost" size="sm" onClick={() => deleteMinistry(m.ministry_id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <Card className="border-slate-200 shadow-sm max-w-xl">
            <CardHeader><CardTitle className="text-lg font-['Outfit']">Manage Services & Events</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <Input data-testid="new-event-name" value={newEvent.name} onChange={e => setNewEvent(f => ({ ...f, name: e.target.value }))} placeholder="Event name" className="border-slate-300" />
                <Select value={newEvent.day_of_week} onValueChange={v => setNewEvent(f => ({ ...f, day_of_week: v }))}>
                  <SelectTrigger data-testid="new-event-day" className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="add-event" onClick={addEvent} className="bg-blue-700 hover:bg-blue-800"><Plus className="h-4 w-4 mr-1" />Add</Button>
              </div>
              <div className="space-y-2">
                {events.map(e => (
                  <div key={e.event_id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50">
                    {editEvent === e.event_id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input value={editEventData.name} onChange={ev => setEditEventData(d => ({ ...d, name: ev.target.value }))} className="h-8 border-slate-300 flex-1" />
                        <Select value={editEventData.day_of_week} onValueChange={v => setEditEventData(d => ({ ...d, day_of_week: v }))}>
                          <SelectTrigger className="h-8 w-32 border-slate-300"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input value={editEventData.time} onChange={ev => setEditEventData(d => ({ ...d, time: ev.target.value }))} className="h-8 w-28 border-slate-300" />
                        <Button size="sm" variant="ghost" onClick={() => saveEditEvent(e.event_id)} className="h-7 w-7 p-0 text-emerald-600"><Check className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditEvent(null)} className="h-7 w-7 p-0 text-slate-400"><X className="h-3 w-3" /></Button>
                      </div>
                    ) : (
                      <>
                        <div>
                          <span className="text-sm font-medium text-slate-700">{e.name}</span>
                          <span className="text-xs text-slate-500 ml-2">{e.day_of_week} at {e.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {e.is_default && <Badge variant="outline" className="text-xs border-blue-200 text-blue-700">Default</Badge>}
                          <div className="flex items-center gap-1">
                            <Button data-testid={`edit-event-${e.event_id}`} variant="ghost" size="sm" onClick={() => startEditEvent(e)} className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 h-7 w-7 p-0">
                              <Edit className="h-3 w-3" />
                            </Button>
                            {!e.is_default && (
                              <Button data-testid={`delete-event-${e.event_id}`} variant="ghost" size="sm" onClick={() => deleteEvent(e.event_id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users" className="mt-4">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-['Outfit']">User Management</CardTitle>
                  <Button data-testid="add-user-btn" size="sm" onClick={() => setAddUserOpen(true)} className="bg-blue-700 hover:bg-blue-800"><Plus className="h-3 w-3 mr-1" />Add User</Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200">
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500">Name</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500">Role</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-slate-500">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.user_id} className="border-slate-100">
                        <TableCell className="text-sm">{u.name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${u.role === 'admin' ? 'bg-purple-100 text-purple-800 hover:bg-purple-100' : u.role === 'pastor' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-slate-100 text-slate-700 hover:bg-slate-100'}`}>
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Select value={u.role} onValueChange={v => changeRole(u.user_id, v)}>
                              <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-32 h-8 border-slate-300"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="pastor">Pastor</SelectItem>
                                <SelectItem value="user">Standard User</SelectItem>
                              </SelectContent>
                            </Select>
                            {u.user_id !== user?.user_id && (
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0" onClick={async () => {
                                if (!window.confirm(`Delete user ${u.name}?`)) return;
                                try { await api.delete(`/users/${u.user_id}`); setUsers(prev => prev.filter(x => x.user_id !== u.user_id)); toast.success('User deleted'); }
                                catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                              }}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            {addUserOpen && (
              <Card className="border-slate-200 shadow-sm mt-4 max-w-md">
                <CardHeader><CardTitle className="text-lg font-['Outfit']">Add New User</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label>Name</Label><Input data-testid="new-user-name" value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Email</Label><Input data-testid="new-user-email" type="email" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Password</Label><Input data-testid="new-user-password" type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} className="mt-1" /></div>
                  <div>
                    <Label>Role</Label>
                    <Select value={newUser.role} onValueChange={v => setNewUser(u => ({ ...u, role: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="pastor">Pastor</SelectItem>
                        <SelectItem value="user">Standard User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button className="bg-blue-700 hover:bg-blue-800" onClick={async () => {
                      try {
                        const res = await api.post('/users', newUser);
                        setUsers(prev => [...prev, res.data]);
                        setAddUserOpen(false);
                        setNewUser({ email: '', password: '', name: '', role: 'user' });
                        toast.success('User created');
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                    }}>Create User</Button>
                    <Button variant="outline" onClick={() => setAddUserOpen(false)}>Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}

        <TabsContent value="touchpoint-rules" className="mt-4">
          <Card className="border-slate-200 shadow-sm max-w-2xl">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Touchpoint Alert Rules</CardTitle>
              <CardDescription>Configure when people get flagged for follow-up based on category, frequency, and trigger events</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(touchpointRules.rules || []).map((rule, idx) => (
                <div key={`rule-${rule.person_type}-${rule.trigger}-${idx}`} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 flex-wrap">
                  <Select value={rule.person_type} onValueChange={v => setTouchpointRules(prev => ({ ...prev, rules: prev.rules.map((r, i) => i === idx ? { ...r, person_type: v } : r) }))}>
                    <SelectTrigger className="w-32 h-8 border-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="visitor">Visitor</SelectItem>
                      <SelectItem value="occasional">Occasional</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={rule.trigger} onValueChange={v => setTouchpointRules(prev => ({ ...prev, rules: prev.rules.map((r, i) => i === idx ? { ...r, trigger: v } : r) }))}>
                    <SelectTrigger className="w-44 h-8 border-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_contact">No contact for X days</SelectItem>
                      <SelectItem value="missed_attendance">Missed X weeks in a row</SelectItem>
                    </SelectContent>
                  </Select>
                  {rule.trigger === 'no_contact' ? (
                    <div className="flex items-center gap-1">
                      <Input type="number" value={rule.days_without_contact || 30} onChange={e => setTouchpointRules(prev => ({ ...prev, rules: prev.rules.map((r, i) => i === idx ? { ...r, days_without_contact: parseInt(e.target.value) || 30 } : r) }))} className="w-16 h-8 border-slate-300" min={1} />
                      <span className="text-xs text-slate-500">days</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Input type="number" value={rule.missed_weeks || 2} onChange={e => setTouchpointRules(prev => ({ ...prev, rules: prev.rules.map((r, i) => i === idx ? { ...r, missed_weeks: parseInt(e.target.value) || 2 } : r) }))} className="w-16 h-8 border-slate-300" min={1} />
                      <span className="text-xs text-slate-500">weeks</span>
                    </div>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 ml-auto" onClick={() => setTouchpointRules(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }))}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setTouchpointRules(prev => ({ ...prev, rules: [...prev.rules, { person_type: 'regular', days_without_contact: 30, trigger: 'no_contact', enabled: true }] }))}>
                  <Plus className="h-3 w-3 mr-1" />Add Rule
                </Button>
                <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={async () => {
                  try { await api.put('/settings/touchpoint-rules', touchpointRules); toast.success('Touchpoint rules saved'); }
                  catch { toast.error('Failed to save'); }
                }}>Save Rules</Button>
                <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100 ml-auto" onClick={async () => {
                  if (!window.confirm('Clear all outstanding touchpoints? This marks everyone as contacted today.')) return;
                  try { await api.post('/touchpoints/clear-all'); toast.success('All touchpoints cleared'); }
                  catch { toast.error('Failed'); }
                }}>Clear All Touchpoints</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
